/**
 * Admin 2.0 (admin.nuvvu.eu; fase 3; 1.0: 26 páginas en el mismo bundle que la tienda). Solo entra
 * quien tiene el claim `admin` (admin-grantRole). Solo español (decisión 13/10). Cada acción pasa por
 * una callable que escribe en `auditLog`; el panel lee Firestore directamente con el claim.
 */
import { useEffect, useState, type ReactElement } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { SandboxBanner, tokens } from '@nuvvu/ui';
import { isSandbox } from './env.js';
import { logOut, signIn, useAdminSession } from './auth.js';
import { btn } from './lib/ui.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { UsersPage, UserPage } from './pages/UsersPage.js';
import { OrdersPage, OrderPage } from './pages/OrdersPage.js';
import { DisputesPage } from './pages/DisputesPage.js';
import { WithdrawalsPage } from './pages/WithdrawalsPage.js';
import { CarriersPage, CarrierPage } from './pages/CarriersPage.js';
import { CampaignsPage } from './pages/CampaignsPage.js';
import { AdsPage } from './pages/AdsPage.js';
import { ReviewsPage } from './pages/ReviewsPage.js';
import { VisitorsPage } from './pages/VisitorsPage.js';
import { SystemPage } from './pages/SystemPage.js';
import { OutreachPage } from './pages/OutreachPage.js';
import { SurveyPage } from './pages/SurveyPage.js';
import { DiagnosticsPage } from './pages/DiagnosticsPage.js';
import { ListingAiPage } from './pages/ListingAiPage.js';
import { ModerationPage } from './pages/ModerationPage.js';
import { RevenuePage } from './pages/RevenuePage.js';
import { SubscriptionsPage } from './pages/SubscriptionsPage.js';
import { MonetizationPage } from './pages/MonetizationPage.js';
import { SupportPage } from './pages/SupportPage.js';
import { ForumPage } from './pages/ForumPage.js';
import { NewsletterPage } from './pages/NewsletterPage.js';
import { CompliancePage } from './pages/CompliancePage.js';
import { StaffPage } from './pages/StaffPage.js';
import { MetricsPage } from './pages/MetricsPage.js';
import { SocialPage } from './pages/SocialPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { ListingsPage } from './pages/ListingsPage.js';

const NAV: readonly { to: string; label: string }[] = [
  { to: '/', label: 'Panel' },
  { to: '/metricas', label: 'Métricas' },
  { to: '/visitantes', label: 'Visitantes' },
  { to: '/usuarios', label: 'Usuarios' },
  { to: '/anuncios', label: 'Anuncios' },
  { to: '/pedidos', label: 'Pedidos' },
  { to: '/incidencias', label: 'Incidencias' },
  { to: '/moderacion', label: 'Moderación' },
  { to: '/valoraciones', label: 'Valoraciones' },
  { to: '/anuncios-ia', label: 'Anuncios con IA' },
  { to: '/soporte', label: 'Soporte' },
  { to: '/foro', label: 'Foro' },
  { to: '/newsletter', label: 'Newsletter' },
  { to: '/correo', label: 'Correo' },
  { to: '/encuesta', label: 'Encuesta' },
  { to: '/redes', label: 'Redes sociales' },
  { to: '/cumplimiento', label: 'Cumplimiento' },
  { to: '/retiradas', label: 'Retiradas' },
  { to: '/reportes', label: 'Reportes' },
  { to: '/ingresos', label: 'Ingresos' },
  { to: '/suscripciones', label: 'Suscripciones' },
  { to: '/monetizacion', label: 'Monetización' },
  { to: '/transportistas', label: 'Transportistas' },
  { to: '/campanas', label: 'Campañas de envío' },
  { to: '/publicidad', label: 'Publicidad' },
  { to: '/ajustes/fees', label: 'Ajustes' },
  { to: '/sistema', label: 'Sistema' },
  { to: '/diagnosticos', label: 'Diagnósticos' },
  { to: '/equipo', label: 'Equipo' },
  { to: '/auditoria', label: 'Auditoría' },
];

/** Móvil (app «nuvvu Admin» o navegador estrecho): menú en un cajón que se abre con ☰. */
function useNarrow(): boolean {
  const q = '(max-width: 760px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = (): void => {
      setNarrow(m.matches);
    };
    m.addEventListener('change', on);
    return () => {
      m.removeEventListener('change', on);
    };
  }, []);
  return narrow;
}

function Gate(): ReactElement {
  const session = useAdminSession();
  const narrow = useNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);
  if (session === undefined) return <p style={{ padding: 32, textAlign: 'center' }}>Cargando…</p>;
  if (session === null)
    return (
      <main style={{ padding: 32, maxWidth: 420, margin: '0 auto', display: 'grid', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Nuvvu Admin 2.0</h1>
        <p>Entra con la cuenta de Google que tiene permisos de administración.</p>
        <button type="button" style={btn(true)} onClick={() => void signIn()}>
          Entrar con Google
        </button>
      </main>
    );
  if (!session.isAdmin)
    return (
      <main style={{ padding: 32, maxWidth: 480, margin: '0 auto', display: 'grid', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Sin permisos</h1>
        <p>
          La cuenta <strong>{session.user.email}</strong> no tiene el rol de administrador. Pide a
          un admin que lo conceda (admin-grantRole) y vuelve a entrar.
        </p>
        <button type="button" style={btn(false)} onClick={() => void logOut()}>
          Salir
        </button>
      </main>
    );
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '220px 1fr',
        minHeight: '100vh',
      }}
    >
      {narrow && (
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: tokens.color.card,
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          <button
            type="button"
            aria-label="Menú"
            onClick={() => {
              setMenuOpen((o) => !o);
            }}
            style={{ ...btn(false), padding: '6px 12px', fontSize: 18 }}
          >
            ☰
          </button>
          <Link
            to="/"
            style={{ fontWeight: 800, color: tokens.color.primary, textDecoration: 'none' }}
          >
            nuvvu admin
          </Link>
        </header>
      )}
      {narrow && menuOpen && (
        <div
          onClick={() => {
            setMenuOpen(false);
          }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 29 }}
        />
      )}
      <aside
        style={{
          borderRight: `1px solid ${tokens.color.border}`,
          padding: 16,
          display: narrow && !menuOpen ? 'none' : 'grid',
          alignContent: 'start',
          gap: 4,
          background: tokens.color.card,
          ...(narrow
            ? {
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                width: '78vw',
                maxWidth: 300,
                overflowY: 'auto',
                zIndex: 30,
                boxShadow: '0 0 24px rgba(0,0,0,.2)',
              }
            : {}),
        }}
      >
        <Link
          to="/"
          style={{
            fontWeight: 800,
            fontSize: 18,
            color: tokens.color.primary,
            textDecoration: 'none',
            marginBottom: 8,
          }}
        >
          nuvvu admin
        </Link>
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/'}
            style={({ isActive }) => ({
              padding: '8px 10px',
              borderRadius: tokens.radius.lg,
              textDecoration: 'none',
              color: isActive ? tokens.color.primaryFg : tokens.color.fg,
              background: isActive ? tokens.color.primary : 'transparent',
              fontSize: 14,
              fontWeight: 600,
            })}
          >
            {n.label}
          </NavLink>
        ))}
        <div
          style={{
            marginTop: 16,
            fontSize: 12,
            color: tokens.color.muted,
            display: 'grid',
            gap: 6,
          }}
        >
          <span>{session.user.email}</span>
          <button type="button" style={btn(false)} onClick={() => void logOut()}>
            Salir
          </button>
        </div>
      </aside>
      <main
        style={{
          padding: narrow ? 12 : 20,
          display: 'grid',
          gap: 16,
          alignContent: 'start',
          minWidth: 0,
        }}
      >
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/usuarios" element={<UsersPage />} />
          <Route path="/usuarios/:uid" element={<UserPage />} />
          <Route path="/anuncios" element={<ListingsPage />} />
          <Route path="/pedidos" element={<OrdersPage />} />
          <Route path="/pedidos/:id" element={<OrderPage />} />
          <Route path="/incidencias" element={<DisputesPage />} />
          <Route path="/retiradas" element={<WithdrawalsPage />} />
          <Route path="/reportes" element={<ReportsPage />} />
          <Route path="/transportistas" element={<CarriersPage />} />
          <Route path="/transportistas/:id" element={<CarrierPage />} />
          <Route path="/campanas" element={<CampaignsPage />} />
          <Route path="/publicidad" element={<AdsPage />} />
          <Route path="/valoraciones" element={<ReviewsPage />} />
          <Route path="/visitantes" element={<VisitorsPage />} />
          <Route path="/sistema" element={<SystemPage />} />
          <Route path="/correo" element={<OutreachPage />} />
          <Route path="/encuesta" element={<SurveyPage />} />
          <Route path="/diagnosticos" element={<DiagnosticsPage />} />
          <Route path="/anuncios-ia" element={<ListingAiPage />} />
          <Route path="/moderacion" element={<ModerationPage />} />
          <Route path="/soporte" element={<SupportPage />} />
          <Route path="/foro" element={<ForumPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/redes" element={<SocialPage />} />
          <Route path="/cumplimiento" element={<CompliancePage />} />
          <Route path="/ingresos" element={<RevenuePage />} />
          <Route path="/suscripciones" element={<SubscriptionsPage />} />
          <Route path="/monetizacion" element={<MonetizationPage />} />
          <Route path="/ajustes/:doc" element={<SettingsPage />} />
          <Route path="/auditoria" element={<AuditPage />} />
          <Route path="/equipo" element={<StaffPage />} />
          <Route path="/metricas" element={<MetricsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function App(): ReactElement {
  return (
    <BrowserRouter>
      {isSandbox && <SandboxBanner text="SANDBOX — admin de pruebas" />}
      <Gate />
    </BrowserRouter>
  );
}
